import { calculateMenuPosition } from './gridCalculations';

/**
 * Compute threshold-based color string for a cell value.
 * Returns 'green', 'yellow', 'orange', 'red', or null if no value/thresholds.
 */
export const computeAutoColor = (value, group, species, organisms = []) => {
  if (value === null || value === undefined || value === '') return null;

  const strValue = String(value).trim();
  if (strValue.startsWith('<') || strValue === '0' || strValue === '+') return 'green';

  const cleanValue = strValue.replace(/[\s\u00A0]/g, '').replace(/,/g, '.');
  const numValue = parseFloat(cleanValue);
  if (isNaN(numValue)) return null;
  if (numValue === 0) return 'green';

  const organism = organisms.find(o =>
    o.group === group && o.species === species && !o.isTotal
  );
  if (!organism || organism.threshold_red == null) return 'green';

  const tY = organism.threshold_yellow || 0;
  const tO = organism.threshold_orange || 0;
  const tR = organism.threshold_red || 0;

  if (numValue >= tR) return 'red';
  if (numValue >= tO) return 'orange';
  if (numValue >= tY) return 'yellow';
  return 'green';
};

/**
 * Update grid value, recalculate sums, and auto-set color from thresholds
 */
export const updateGridValueHelper = (organismKey, location, value, calculateSums, setGridData, organisms, setColorOverrides) => {
  setGridData(prev => {
    const updated = {
      ...prev,
      [organismKey]: {
        ...prev[organismKey],
        [location]: value
      }
    };
    
    // Auto-calculate sums
    return calculateSums(updated);
  });

  // Auto-compute color override from thresholds
  if (organisms && setColorOverrides) {
    const parts = organismKey.split('_');
    const group = parts[0] || '';
    const species = parts.slice(1).join('_') || '';
    const overrideKey = `${organismKey}_${location}`;
    
    // Find the organism to check if thresholds are loaded
    const organism = organisms.find(o => o.group === group && o.species === species && !o.isTotal);
    const hasThresholds = organism && organism.threshold_red != null;
    
    // Empty cell → always remove override regardless of threshold state
    if (value === null || value === undefined || value === '') {
      setColorOverrides(prev => {
        const updated = { ...prev };
        delete updated[overrideKey];
        return updated;
      });
    } else if (hasThresholds || !organism) {
      // Thresholds loaded OR organism unknown (custom) → compute & save
      const color = computeAutoColor(value, group, species, organisms);
      if (color) {
        setColorOverrides(prev => ({ ...prev, [overrideKey]: color }));
      }
    } else {
      // Organism exists but thresholds not loaded yet →
      // DO NOT save a (possibly wrong) 'green' override. Clear any stale override
      // and let the ThresholdRecalculation effect fix it when organisms arrive.
      setColorOverrides(prev => {
        if (!(overrideKey in prev)) return prev;
        const updated = { ...prev };
        delete updated[overrideKey];
        return updated;
      });
    }

    // Also update sum row color if this organism belongs to a group with a sum row
    if (group === 'Kiselalger' && hasThresholds) {
      // Need to recalculate the sum for this location and set its color
      // We do this by reading the latest gridData after sums are calculated
      setTimeout(() => {
        setGridData(currentGrid => {
          const sumKey = `${group}_${group.toUpperCase()} SUM`;
          const sumValue = currentGrid[sumKey]?.[location];
          if (sumValue) {
            const sumColor = computeAutoColor(sumValue, group, `${group.toUpperCase()} SUM`, organisms);
            const sumOverrideKey = `${sumKey}_${location}`;
            if (sumColor) {
              setColorOverrides(prev => ({ ...prev, [sumOverrideKey]: sumColor }));
            }
          }
          return currentGrid; // Don't modify
        });
      }, 0);
    }
  }
};

/**
 * Recompute all color overrides for a grid based on current organisms + thresholds.
 * Call this after organisms are (re)loaded to fix any stale/missing overrides.
 * Respects manual overrides: only updates overrides that look auto-computed (i.e., match a threshold result or are missing).
 * Returns a new colorOverrides object.
 */
export const recomputeAllColorOverrides = (gridData, organisms, existingOverrides = {}) => {
  if (!gridData || !organisms || organisms.length === 0) return existingOverrides;
  const next = { ...existingOverrides };
  let changed = false;
  
  Object.entries(gridData).forEach(([organismKey, locationData]) => {
    if (!locationData || typeof locationData !== 'object') return;
    const parts = organismKey.split('_');
    const group = parts[0] || '';
    const species = parts.slice(1).join('_') || '';
    
    Object.entries(locationData).forEach(([loc, value]) => {
      const overrideKey = `${organismKey}_${loc}`;
      const autoColor = computeAutoColor(value, group, species, organisms);
      
      if (autoColor == null) {
        // Empty/invalid → ensure no override
        if (overrideKey in next) {
          delete next[overrideKey];
          changed = true;
        }
        return;
      }
      
      const current = next[overrideKey];
      // Only set/replace when:
      //  - No existing override, OR
      //  - Existing override is 'green' AND auto says non-green (likely a stale race-condition value)
      // This preserves explicit user overrides (non-green → something else)
      if (current == null || (current === 'green' && autoColor !== 'green')) {
        if (next[overrideKey] !== autoColor) {
          next[overrideKey] = autoColor;
          changed = true;
        }
      }
    });
  });
  
  return changed ? next : existingOverrides;
};

/**
 * Update zooplankton value with normalization
 */
export const updateZooplanktonValueHelper = (organismKey, location, value, setZooplanktonGridData) => {
  // Normalize the value for storage
  let normalizedValue = value;
  
  // Handle special cases
  if (value === '<5' || value === '< 5' || value === '<5000' || value === '< 5000') {
    normalizedValue = '<5'; // Standardize to <5 format
  } else if (value === '+') {
    normalizedValue = '+'; // Keep + as-is (oppdaget, men ikke kvantifisert)
  } else if (value && value.trim()) {
    // Try to parse as number
    const numValue = value.replace(/,/g, '').trim();
    if (!isNaN(numValue) && numValue !== '') {
      normalizedValue = numValue;
    }
  }
  
  setZooplanktonGridData(prev => ({
    ...prev,
    [organismKey]: {
      ...prev[organismKey],
      [location]: normalizedValue
    }
  }));
};

/**
 * Handle right click on cell
 */
export const handleRightClickHelper = (e, organismKey, location, setContextMenu) => {
  e.preventDefault();
  e.stopPropagation(); // Prevent event from bubbling to row handler
  
  const { x, y } = calculateMenuPosition(e.clientX, e.clientY, 200, 300);
  
  setContextMenu({
    x,
    y,
    organismKey,
    location
  });
};

/**
 * Handle right click on row
 */
export const handleRowRightClickHelper = (e, organismKey, organism, setContextMenu) => {
  e.preventDefault();
  
  const { x, y } = calculateMenuPosition(e.clientX, e.clientY, 200, 100);
  
  setContextMenu({
    x,
    y,
    organismKey,
    isRowContext: true,
    organism
  });
};

/**
 * Set color override for cell
 */
export const setColorOverrideHelper = (contextMenu, color, setColorOverrides, setContextMenu) => {
  if (!contextMenu) return;
  
  const overrideKey = `${contextMenu.organismKey}_${contextMenu.location}`;
  setColorOverrides(prev => ({
    ...prev,
    [overrideKey]: color
  }));
  setContextMenu(null);
};

/**
 * Remove color override for cell
 */
export const removeColorOverrideHelper = (contextMenu, setColorOverrides, setContextMenu) => {
  if (!contextMenu) return;
  
  const overrideKey = `${contextMenu.organismKey}_${contextMenu.location}`;
  setColorOverrides(prev => {
    const updated = { ...prev };
    delete updated[overrideKey];
    return updated;
  });
  setContextMenu(null);
};

/**
 * Update depth for organism
 */
export const updateDepthHelper = (organismKey, newDepth, setDepthOverrides) => {
  setDepthOverrides(prev => ({
    ...prev,
    [organismKey]: newDepth
  }));
};
